// ============================================================================
// HR Onboarding — pure helpers. No React, no supabase. Unit-testable.
// ============================================================================
import {
  ONBOARDING_STAGES,
  CHECKLIST_SECTIONS,
  canonicalSeed,
  checklistLabel,
  type CanonicalChecklist,
  type ChecklistItem,
  type ChecklistSection,
  type OnboardingStage,
  type OnboardingType,
} from './types';

// ----- Status → display stage (render-only mapping) -------------------------
// Legacy partner_onboarding free-text values get mapped to canonical stages.
// We flag unmapped values so the session report / console surfaces drift.
const LEGACY_STAGE_MAP: Record<string, OnboardingStage> = {
  in_progress: 'Documents Received',
  Unknown: 'Offer Accepted',
  Incomplete: 'Documents Sent',
};

const TERMINAL_SET = new Set(['Cancelled', 'Complete', 'Completed']);

export interface StageMapping {
  stage: OnboardingStage;
  terminal: boolean;
  unmapped: boolean;
}

export function mapStatusToStage(raw: string | null | undefined): StageMapping {
  if (!raw) return { stage: 'Offer Accepted', terminal: false, unmapped: true };
  if (TERMINAL_SET.has(raw)) {
    // Complete/Completed land on Active for display purposes; Cancelled is off-board.
    return { stage: 'Active', terminal: true, unmapped: false };
  }
  if ((ONBOARDING_STAGES as readonly string[]).includes(raw)) {
    return { stage: raw as OnboardingStage, terminal: false, unmapped: false };
  }
  const mapped = LEGACY_STAGE_MAP[raw];
  if (mapped) return { stage: mapped, terminal: false, unmapped: false };
  // Unknown value — best-effort default, flagged.
  return { stage: 'Offer Accepted', terminal: false, unmapped: true };
}

// ----- Checklist parsing ----------------------------------------------------
// Canonical shape = an object with at least one of the four section keys
// holding an array. The live partner_onboarding blobs (source / last_backfilled
// / coi_link / notes) are NOT canonical → return null so callers fall back to
// the seed for rendering while we preserve the raw blob for merge-on-write.
function isChecklistItemArray(v: unknown): v is ChecklistItem[] {
  return (
    Array.isArray(v) &&
    v.every(
      (x) =>
        x &&
        typeof x === 'object' &&
        typeof (x as ChecklistItem).key === 'string' &&
        typeof (x as ChecklistItem).status === 'string',
    )
  );
}

export function parseChecklist(
  raw: Record<string, unknown> | null | undefined,
): CanonicalChecklist | null {
  if (!raw || typeof raw !== 'object') return null;
  const hasAnySection = CHECKLIST_SECTIONS.some((s) => isChecklistItemArray(raw[s]));
  if (!hasAnySection) return null;
  // Build a canonical object, normalizing each section (default applies_to to
  // all types when missing so a malformed item still renders rather than
  // crashing — graceful-degrade per the prompt's handoff contract).
  const seed = canonicalSeed();
  const out = {} as CanonicalChecklist;
  for (const section of CHECKLIST_SECTIONS) {
    const arr = raw[section];
    if (isChecklistItemArray(arr)) {
      out[section] = arr.map((it) => ({
        key: it.key,
        label: it.label ?? it.key,
        status: it.status ?? 'pending',
        applies_to:
          Array.isArray(it.applies_to) && it.applies_to.length
            ? (it.applies_to as OnboardingType[])
            : ['W2', 'Partner', 'Subcontractor'],
        notes: it.notes,
      }));
    } else {
      // Section missing in the blob — fall back to the seed for that section.
      out[section] = seed[section];
    }
  }
  return out;
}

// Resolve the checklist a record should RENDER with: canonical if present,
// else the seed.
export function effectiveChecklist(
  parsed: CanonicalChecklist | null,
): CanonicalChecklist {
  return parsed ?? canonicalSeed();
}

// Merge canonical sections back into the raw blob, preserving non-section keys
// (source / last_backfilled / coi_link / notes etc.) so we never destroy the
// backfill metadata that 36 partner rows already carry.
export function mergeChecklistIntoRaw(
  raw: Record<string, unknown> | null,
  checklist: CanonicalChecklist,
): Record<string, unknown> {
  const base = raw && typeof raw === 'object' ? { ...raw } : {};
  for (const section of CHECKLIST_SECTIONS) {
    base[section] = checklist[section];
  }
  return base;
}

// One checklist item set to a status (the panel's tap, and Ask AI's card).
export function withItemStatus(
  checklist: CanonicalChecklist,
  key: string,
  status: ChecklistItem['status'],
): CanonicalChecklist {
  const next = {} as CanonicalChecklist;
  for (const section of CHECKLIST_SECTIONS) {
    next[section] = checklist[section].map((it) => (it.key === key ? { ...it, status } : it));
  }
  return next;
}

// A note appended to the record's notes, stamped with the day (the panel's Add Note).
export function appendNote(notes: string | null, text: string, stamp: string): string {
  return `${notes ? notes + '\n' : ''}[${stamp}] ${text}`;
}

// ----- applies_to filtering -------------------------------------------------
export function visibleItems(
  items: ChecklistItem[],
  type: OnboardingType,
): ChecklistItem[] {
  return items.filter((it) => it.applies_to.includes(type));
}

export function allVisibleItems(
  checklist: CanonicalChecklist,
  type: OnboardingType,
): { section: ChecklistSection; item: ChecklistItem }[] {
  const out: { section: ChecklistSection; item: ChecklistItem }[] = [];
  for (const section of CHECKLIST_SECTIONS) {
    for (const item of visibleItems(checklist[section], type)) {
      out.push({ section, item });
    }
  }
  return out;
}

// ----- Progress -------------------------------------------------------------
// Percent of applies_to-visible items where status='complete'. Items with
// status='n_a' are excluded from the denominator.
export function computeProgress(
  checklist: CanonicalChecklist,
  type: OnboardingType,
): number {
  const visible = allVisibleItems(checklist, type).map((x) => x.item);
  const denom = visible.filter((it) => it.status !== 'n_a').length;
  if (denom === 0) return 0;
  const complete = visible.filter((it) => it.status === 'complete').length;
  return Math.round((100 * complete) / denom);
}

// First N pending/blocked items in section-then-key order.
export function blockingItems(
  checklist: CanonicalChecklist,
  type: OnboardingType,
  n = 2,
): ChecklistItem[] {
  return allVisibleItems(checklist, type)
    .map((x) => x.item)
    .filter((it) => it.status === 'pending' || it.status === 'blocked')
    .slice(0, n);
}

export function missingItemsSummary(
  checklist: CanonicalChecklist,
  type: OnboardingType,
): string[] {
  return allVisibleItems(checklist, type)
    .map((x) => x.item)
    .filter((it) => it.status === 'pending' || it.status === 'blocked')
    .map((it) => checklistLabel(it.key, it.label, type));
}

// ----- Dates / priority -----------------------------------------------------
export function daysBetween(fromISO: string | null | undefined): number | null {
  if (!fromISO) return null;
  const d = new Date(fromISO + 'T00:00:00Z');
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((d.getTime() - today) / 86_400_000);
}

// days since start: positive when start is in the past.
export function daysSinceStart(startDate: string | null | undefined): number | null {
  const delta = daysBetween(startDate);
  return delta === null ? null : -delta;
}

export type Priority = 'HIGH' | 'MEDIUM';

// HIGH if start_date is within 5 days (or already passed) AND progress < 80%.
export function computePriority(
  startDate: string | null | undefined,
  progress: number,
): Priority {
  const daysUntil = daysBetween(startDate);
  if (daysUntil !== null && daysUntil < 5 && progress < 80) return 'HIGH';
  return 'MEDIUM';
}

// ----- COI traffic light (prompt §3.3 spec) ---------------------------------
// green: > today + 30d | amber(yellow): today..today+30 | red: null or < today.
// NOTE: same thresholds as v_provider_compliance_status and
// shared/coi-state.ts coiStateTone(). (The old 30/60-day coiStatus() in
// src/modules/hr/utils.ts that this note used to contrast with is gone.)
export type CoiLight = 'green' | 'amber' | 'red';

export function coiTrafficLight(coi: string | null | undefined): CoiLight {
  const days = daysBetween(coi);
  if (days === null) return 'red';
  if (days < 0) return 'red';
  if (days <= 30) return 'amber';
  return 'green';
}

export const COI_LIGHT_CLASS: Record<CoiLight, string> = {
  green: 'bg-st-ok-solid',
  amber: 'bg-st-warn-solid',
  red: 'bg-st-danger-solid',
};

export const COI_LIGHT_LABEL: Record<CoiLight, string> = {
  green: 'Current',
  amber: 'Expiring ≤30d',
  red: 'Missing / Expired',
};

// ----- Formatting -----------------------------------------------------------
export function fmtDate(d: string | null | undefined): string {
  if (!d) return '—';
  const dt = new Date(d + 'T00:00:00Z');
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function fmtDateShort(d: string | null | undefined): string {
  if (!d) return '—';
  const dt = new Date(d + 'T00:00:00Z');
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

// ----- CSV ------------------------------------------------------------------
export function csvEscape(v: string | number | null | undefined): string {
  const s = v === null || v === undefined ? '' : String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
